/* eslint-disable react-hooks/exhaustive-deps */
import React, { useContext, useState, useEffect } from "react";
import clsx from "clsx";
import { GblContext } from "providers/formatter";
import { Loading, useTranslate, useRedirect } from "react-admin";
import { Box, FormControl, Select, InputLabel, MenuItem, Chip } from "@material-ui/core";
import { makeStyles } from "@material-ui/core/styles";
import MuiTranslateTable from "components/MuiTranslateTable";
import CustomTablePagination from "components/CustomTablePagination";
import DatePickerFormat from "components/DatePickerFormat";
import ModelUriQueryString from "models/uriQueryString";
import { addDays, startOfMonth } from "date-fns";
import SnackbarUtils from "utils/SnackbarUtils";
import VisibilityIcon from "@material-ui/icons/Visibility";
import { getCountingDocumentSearchList } from "services/asset";

const useStyles = makeStyles((theme) => ({
  root: {
    width: "100%",
  },
  statusDraft: {
    backgroundColor: "#ff9800",
    color: "white",
  },
  statusCommitted: {
    backgroundColor: "#4caf50",
    color: "white",
  },
  statusVoid: {
    backgroundColor: "#e57373",
    color: "white",
  },
  filterBox: {
    display: "flex",
    flexWrap: "wrap",
    gap: theme.spacing(2),
    marginBottom: theme.spacing(1),
    alignItems: "center",
  },
  formControl: {
    minWidth: 150,
  },
}));

const List = (props) => {
  const { basePath } = props;
  const translate = useTranslate();
  const redirect = useRedirect();
  const classes = useStyles();
  const { settingAll, ToMySqlDate, DateToString } = useContext(GblContext);
  const ClosePeriodAsset = settingAll.SettingClosePeriod?.ClosePeriodAsset;
  
  const [Data, setData] = useState([]);
  const [Paging, setPaging] = useState();
  const [loading, setLoading] = useState(true);
  const [uriQueryString, setUriQueryString] = useState({
    Limit: ModelUriQueryString.Limit,
    Page: ModelUriQueryString.Page,
    OrderBy: ModelUriQueryString.OrderBy || { DocNo: "desc" },
    WhereGroupList: ModelUriQueryString.WhereGroupList,
    Exclude: ModelUriQueryString.Exclude,
    WhereRaw: ModelUriQueryString.WhereRaw,
    WhereLike: ModelUriQueryString.WhereLike,
    WhereLikeFields: ModelUriQueryString.WhereLikeFields,
  });
  const [searchText, setSearchText] = useState();
  const [status, setStatus] = useState("All");
  const [filterDate, setFilterDate] = useState({
    from: startOfMonth(addDays(new Date(ClosePeriodAsset || new Date()), 1)),
    to: new Date(),
  });

  const handleFilterList = () => {
    let fromMySqlDate = ToMySqlDate(filterDate.from);
    let toMySqlDate = ToMySqlDate(filterDate.to);

    const conditions = [
      {
        AndOr: "And",
        Field: "CountDate",
        Operator: ">=",
        Value: fromMySqlDate,
      },
      {
        AndOr: "And",
        Field: "CountDate",
        Operator: "<=",
        Value: toMySqlDate,
      },
    ];

    if (status !== "All") {
      conditions.push({
        AndOr: "And",
        Field: "Status",
        Operator: "=",
        Value: status,
      });
    }

    uriQueryString.WhereGroupList = [
      {
        AndOr: "And",
        ConditionList: conditions,
      },
    ];

    uriQueryString.WhereLikeFields = ["DocNo", "Location"];
  };

  const fetchSearchList = async (qString) => {
    setLoading(true);
    handleFilterList();
    try {
      const response = await getCountingDocumentSearchList(qString);
      if (response && response.Data) {
        setData(response.Data);
        setPaging(response.Paging);
        setUriQueryString(qString);
      } else {
        setData([]);
      }
    } catch (e) {
      SnackbarUtils.error(e.message || "Failed to fetch counting documents");
      setData([]);
    }
    setLoading(false);
  };

  const handleRequestSort = (property, order) => {
    uriQueryString.OrderBy = { [property]: order };
    fetchSearchList(uriQueryString);
  };

  const handleChangePage = (e, newPage) => {
    uriQueryString.Page = newPage + 1;
    fetchSearchList(uriQueryString);
  };

  const handleChangeRowsPerPage = (e) => {
    let newRowsPerPage = parseInt(e.target.value);
    uriQueryString.Limit = newRowsPerPage;
    uriQueryString.Page = 1;
    fetchSearchList(uriQueryString);
  };

  const handleTableChange = (action, tableState) => {
    if (action === "onSearchClose") {
      setSearchText();
      uriQueryString.WhereLike = "";
      uriQueryString.WhereLikeFields = [];
      fetchSearchList(uriQueryString);
    }
  };

  const onSearchText = (text) => {
    setSearchText(text);
    uriQueryString.WhereLike = text;
    uriQueryString.Page = 1;
    fetchSearchList(uriQueryString);
  };

  useEffect(() => {
    fetchSearchList(uriQueryString);
  }, [status, filterDate]);

  const columns = [
    {
      name: "DocNo",
      label: " ",
      options: {
        filter: false,
        sort: false,
        customBodyRender: (value) => {
          return (
            <VisibilityIcon
              fontSize="small"
              color="primary"
              style={{ cursor: "pointer" }}
              onClick={() => redirect("show", basePath, value)}
            />
          );
        },
      },
    },
    {
      name: "DocNo",
      label: translate("ra.field.Doc No"),
      options: {
        filter: true,
        sort: true,
        customBodyRender: (value) => {
          return (
            <a
              href={`#/countingDocument/${value}/show`}
              style={{ color: "#2196f3", textDecoration: "none", fontWeight: "bold" }}
            >
              {value}
            </a>
          );
        },
      },
    },
    {
      name: "Location",
      label: translate("ra.field.Location"),
      options: {
        filter: true,
        sort: true,
      },
    },
    {
      name: "CountDate",
      label: translate("ra.field.Count Date"),
      options: {
        filter: true,
        sort: true,
        customBodyRender: (value) => {
          return value ? DateToString(new Date(value)) : "";
        },
      },
    },
    {
      name: "Status",
      label: translate("ra.field.Status"),
      options: {
        filter: true,
        sort: true,
        customBodyRender: (value) => {
          return (
            <Chip
              label={value}
              size="small"
              className={clsx({
                [classes.statusDraft]: value === "Draft",
                [classes.statusCommitted]: value === "Committed",
                [classes.statusVoid]: value === "Void",
              })}
            />
          );
        },
      },
    },
  ];

  const tableOptions = {
    filter: false,
    responsive: "standard",
    selectableRows: "none",
    serverSide: true,
    confirmFilters: true,
    searchText: searchText,
    searchProps: {
      onSearch: (text) => onSearchText(text),
    },
    onTableChange: (action, tableState) => handleTableChange(action, tableState),
    customFooter: () => {
      if (loading) return null;
      return (
        <CustomTablePagination
          rowsPerPageOptions={[10, 20, 50, 100]}
          count={Paging?.TotalRecord || 0}
          rowsPerPage={uriQueryString.Limit}
          page={uriQueryString.Page - 1}
          onChangePage={handleChangePage}
          onChangeRowsPerPage={handleChangeRowsPerPage}
        />
      );
    },
  };

  if (loading && Data.length === 0) {
    return <Loading />;
  }

  return (
    <div className={classes.root}>
      <Box className={classes.filterBox}>
        <DatePickerFormat
          label={translate("ra.field.From")}
          value={filterDate.from}
          onChange={(date) => setFilterDate((prev) => ({ ...prev, from: date }))}
          style={{ width: 170 }}
        />
        <DatePickerFormat
          label={translate("ra.field.To")}
          value={filterDate.to}
          onChange={(date) => setFilterDate((prev) => ({ ...prev, to: date }))}
          style={{ width: 170 }}
        />
        <FormControl variant="outlined" className={classes.formControl} size="small">
          <InputLabel>{translate("ra.field.Status")}</InputLabel>
          <Select
            value={status}
            onChange={(e) => setStatus(e.target.value)}
            label={translate("ra.field.Status")}
          >
            <MenuItem value="All">All</MenuItem>
            <MenuItem value="Draft">Draft</MenuItem>
            <MenuItem value="Committed">Committed</MenuItem>
            <MenuItem value="Void">Void</MenuItem>
          </Select>
        </FormControl>
      </Box>
      <MuiTranslateTable
        title={translate("ra.module.Counting Documents")}
        data={Data}
        columns={columns}
        options={tableOptions}
        onColumnSortChange={handleRequestSort}
      />
    </div>
  );
};

export default List;
